import { useEffect, useRef, useState } from 'react';
import { Toast as ToastType } from '@/shared/contexts/ToastContext';
import { motion, AnimatePresence } from 'framer-motion';

interface ToastProps {
  toast: ToastType;
  onRemove: () => void;
}

const Toast = ({ toast, onRemove }: ToastProps) => {
  const [visible, setVisible] = useState(true);
  const progressRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);
  const animationDuration = 300;
  const visibleDuration = toast.duration - animationDuration;

  useEffect(() => {
    if (toast.persistent) return;

    startTimeRef.current = Date.now();

    const timer = setTimeout(() => setVisible(false), visibleDuration);

    const tick = () => {
      if (!progressRef.current) return;
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, visibleDuration - elapsed);
      progressRef.current.style.width = `${(remaining / visibleDuration) * 100}%`;
      if (elapsed < visibleDuration) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      clearTimeout(timer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [visibleDuration, toast.persistent]);

  useEffect(() => {
    if (!visible) {
      const timer = setTimeout(onRemove, animationDuration);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [visible, onRemove]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={`toast toast--${toast.type}`}
          initial={{ opacity: 0, y: 12, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.94 }}
          transition={{ duration: 0.2 }}
          role="alert"
        >
          {toast.thumbnail && (
            <img className="toast__thumbnail" src={toast.thumbnail} alt="" />
          )}
          <div className="toast__body">
            {toast.href ? (
              <a
                href={toast.href}
                target="_blank"
                rel="noopener noreferrer"
                className="toast__content toast__content--link"
              >
                {toast.title && <h3 className="toast__title">{toast.title}</h3>}
                <p className="toast__message">{toast.message}</p>
              </a>
            ) : (
              <div className="toast__content">
                {toast.title && <h3 className="toast__title">{toast.title}</h3>}
                <p className="toast__message">{toast.message}</p>
              </div>
            )}
            <div
              className="toast__close-button"
              onClick={() => setVisible(false)}
              style={{ color: 'var(--color-white-50)' }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast;
